import { useCallback, useEffect, useState } from "react"
import { dateTimeStampFormat } from "shared/util"

import { useUserSession } from "features/session/context"
import { useSearch } from "features/search/context"
import {
	SisetuResult,
	SisetuSummaryResult,
	useGetSisetuSummary,
	usePostSisetuSummary,
	usePostSisetuSummaryGenerativeAI
} from "interface/sisetu"

import {
	Box, BoxProps, Stack,
	Button, IconButton,
	Typography,
	StackProps,
	Skeleton
} from "@mui/material"
import {
  Assistant as AIIcon
} from '@mui/icons-material'
import { EditIcon, OkIcon, CloseIcon } from "shared/icons"

import { MarkdownPreview } from "components/controls/markDownPreview"
import { MarkdownInput } from "components/controls/markDownInput"
import { ErrorBlock } from "components/controls/errorInfo"
import { BlockCaption } from "components/controls/blockCaption"

export const useSisetuSummary = (
	sisetu: SisetuResult
) => {
	const search = useSearch()
	const { isGeneral, hasGenAI, domain } = useUserSession()

	const [isEditMode, setEditMode] = useState<boolean>(false)

	const [summaryData, setSummaryData] = useState<{
		summary: string
		updateTime?: string
	} | null>(null)

	const [editData, setEditData] = useState<string>("")

	const getState = useGetSisetuSummary(search?.searchId, sisetu.sisetuCode)
	useEffect(
		() => {
			setSummaryData((getState.error || getState.loading || !getState.data) ? null : getState.data)
		},
		[getState]
	)
	const onPosted = useCallback(
		(result: SisetuSummaryResult) => {
			setSummaryData(result)
		},
		[setSummaryData]
	)
	const [postState, post] = usePostSisetuSummary(search?.searchId, sisetu.sisetuCode, {
		onPosted: onPosted
	})
	const onGenAI = useCallback(
		(result: string | null) => {
			if (result) {
				setEditData(result)
			}
		},
		[setEditData]
	)
	const [genAIState, genAI] = usePostSisetuSummaryGenerativeAI(search?.searchId, sisetu.sisetuCode, {
		onPosted: onGenAI
	})

	const onEditStart = useCallback(
		() => {
			setEditData(summaryData?.summary ?? '')
			setEditMode(true)
		},
		[summaryData, setEditData, setEditMode]
	)
	const onApplyEdit = useCallback(
		() => {
			post({
				summary: editData,
				updateTime: summaryData?.updateTime
			})
			setEditMode(false)
		},
		[editData, summaryData, setEditMode, post]
	)
	const onEditCancel = useCallback(
		() => {
			setEditMode(false)
		},
		[setEditMode]
	)

	return {
		isValid: (search?.searchId && sisetu.sisetuCode) ? true : false,
		isEdittable: isGeneral,
		loading: getState.loading || postState.posting || genAIState.posting,
		getError: getState.error,
		postError: postState.error,
		genAIError: genAIState.error,
		isEditMode, setEditMode,
		searchId: search?.searchId,
		summaryData,
		onEditStart, onApplyEdit, onEditCancel, editData, setEditData,
		genAI, hasGenAI, domain
	}
}

type EditSisetuSummaryProps = {
	summary: ReturnType<typeof useSisetuSummary>
} & StackProps
const EditSisetuSummary = ({
	summary,
	...others
}: EditSisetuSummaryProps) => {
	return (
		<Stack {...others} direction="column" padding={1} spacing={1}>
			<MarkdownInput
				size="small"
				value={summary.editData}
				onChange={e => summary.setEditData(e.target.value)}
				rows={18}
			/>
			<ErrorBlock error={summary.genAIError}/>
			<Stack direction="row" spacing={1}>
				{summary.hasGenAI && (
					<IconButton size="small" onClick={() => summary.genAI()}>
						<AIIcon color="primary"/>
					</IconButton>
				)}

				<Button
					variant="contained"
					size="small"
					onClick={summary.onApplyEdit}
					startIcon={<OkIcon/>}
				>
					保存
				</Button>
				<Button
					variant="text"
					size="small"
					onClick={summary.onEditCancel}
					startIcon={<CloseIcon/>}
				>
					キャンセル
				</Button>
			</Stack>
		</Stack>		
	)
}

type ViewSisetuSummaryProps = {
	summary: ReturnType<typeof useSisetuSummary>
	onKojinOpen: (kojinCode: string) => void
} & StackProps
const ViewSisetuSummary = ({
	summary,
	onKojinOpen,
	...others
}: ViewSisetuSummaryProps) => {
	const onLinkClick = useCallback(
		(url: string): boolean => {
			if (!summary.searchId) return false
			const root = `/${summary.domain}/search/${summary.searchId}/kojin/`
			if (!url.startsWith(root)) return false
			const kojinCode = url.substring(root.length)
			onKojinOpen(kojinCode)
			return true
		},
		[onKojinOpen, summary.searchId, summary.domain]
	)
	return summary.loading ? (
		<Skeleton variant="rectangular" width="100%" height={28} />
	) : summary.summaryData ? (
		<Stack {...others} direction="column">
			<BlockCaption
				caption="サマリー"
				rightElement={
					<Stack direction="row" spacing={1} alignItems="center">
						{summary.summaryData?.updateTime ? (
							<Typography variant="caption">{dateTimeStampFormat(summary.summaryData.updateTime)} 更新</Typography>
						) : <></>}

						{summary.isEdittable && (
							<Button
								variant="text"
								size="small"
								onClick={summary.onEditStart}
								startIcon={<EditIcon />}
							>
								サマリーを編集
							</Button>
						)}
					</Stack>
				}
			/>
			<Stack direction="column" spacing={1}>
				<ErrorBlock error={summary.postError}/>
				<ErrorBlock error={summary.genAIError}/>

				{summary.summaryData?.summary && (
					<MarkdownPreview
						value={summary.summaryData?.summary ?? ''}
						emptyText=""
						padding={2}
						onLinkClick={onLinkClick}
					/>
				)}
			</Stack>
		</Stack>
	) : summary.isEdittable ? (
		<Box padding={1}>
			<Button
				variant="text"
				size="small"
				fullWidth
				onClick={summary.onEditStart}
				startIcon={<EditIcon />}
			>
				サマリーを追加
			</Button>
		</Box>
	) : <></>
}

type SisetuSummaryProps = {
	summary: ReturnType<typeof useSisetuSummary>
	onKojinOpen: (kojinCode: string) => void
} & BoxProps
export const SisetuSummary = ({
	summary,
	onKojinOpen,
	...others
}: SisetuSummaryProps) => {
	return (
		<Box {...others}>
			{summary.isEditMode ? (
				<EditSisetuSummary summary={summary}/>
			) : (summary.isEdittable || summary.summaryData?.summary) ? (
				<ViewSisetuSummary summary={summary} onKojinOpen={onKojinOpen}/>
			) : <></>}
		</Box>
	)
}